import React, { useEffect, useState, useRef, useCallback } from 'react';
import { contentAPI } from '../services/api';
import ContentRow from '../components/ContentRow';
import MusicVideoRow from '../components/MusicVideoRow';
import PPVRow from '../components/PPVRow';
import ContinueWatchingRow from '../components/ContinueWatchingRow';
import ContentInfoModal from '../components/ContentInfoModal';
import { Play, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MuxPlayer from '@mux/mux-player-react';

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [featuredContent, setFeaturedContent] = useState([]);
  const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);
  const [trending, setTrending] = useState([]);
  const [newReleases, setNewReleases] = useState([]);
  const [pulseOriginals, setPulseOriginals] = useState([]);
  const [musicVideos, setMusicVideos] = useState([]);
  const [ppvContent, setPpvContent] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      // Get featured content (multiple for cycling)
      const featuredRes = await contentAPI.getAll({ featured: true, limit: 15 });
      if (featuredRes.data.length > 0) {
        // Filter to only show featured content that can autoplay (Mux trailer OR YouTube)
        const featuredAutoplay = featuredRes.data.filter(c => c.trailer_playback_id || c.youtube_id);
        setFeaturedContent(featuredAutoplay.length > 0 ? featuredAutoplay : featuredRes.data);
      }

      // Get all content for different sections
      const allContentRes = await contentAPI.getAll({ limit: 50 });
      const allContent = allContentRes.data;

      // Filter music videos (exclude PPV music videos from this row)
      const musicVids = allContent.filter(c => c.content_type === 'music_video' && !c.is_ppv);
      setMusicVideos(musicVids);

      // Filter PPV content (all types)
      const ppv = allContent.filter(c => c.is_ppv);
      setPpvContent(ppv);

      // Filter non-music, non-PPV content for other rows
      const regularContent = allContent.filter(c => c.content_type !== 'music_video' && !c.is_ppv);

      // Organize content - Trending uses featured_order
      const trendingSorted = [...regularContent].sort((a, b) => (a.featured_order ?? 999) - (b.featured_order ?? 999));
      setTrending(trendingSorted.slice(0, 10));
      setNewReleases(regularContent.slice(0, 10));
      setPulseOriginals(regularContent.filter(c => c.featured).sort((a, b) => (a.featured_order ?? 999) - (b.featured_order ?? 999)).slice(0, 10));
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle video end - cycle to next featured content
  const handleVideoEnd = useCallback(() => {
    if (featuredContent.length > 1) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentFeaturedIndex((prev) => (prev + 1) % featuredContent.length);
        setIsTransitioning(false);
      }, 500);
    }
  }, [featuredContent.length]);

  // Handle dot click for manual navigation
  const handleDotClick = (index) => {
    if (index !== currentFeaturedIndex) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentFeaturedIndex(index);
        setIsTransitioning(false);
      }, 500);
    }
  };

  // Open more info modal
  const handleMoreInfo = (content) => {
    setModalContent(content);
  };

  const currentFeatured = featuredContent[currentFeaturedIndex];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-electric/30 border-t-electric rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Featured Hero Section with Cycling */}
      {currentFeatured && (
        <div className="relative h-[70vh] md:h-[80vh] w-full overflow-hidden">
          {/* Background Video with Wipe Transition */}
          <div 
            className={`absolute inset-0 transition-all duration-500 ease-in-out ${
              isTransitioning ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'
            }`}
          >
            {/* Use Mux trailer if available, then YouTube embed, then HLS video_url, otherwise thumbnail */}
            {currentFeatured.trailer_playback_id ? (
              <MuxPlayer
                key={currentFeatured.id}
                playbackId={currentFeatured.trailer_playback_id}
                poster={currentFeatured.thumbnail}
                autoPlay
                muted
                loop
                streamType="on-demand"
                style={{ 
                  width: '100%', 
                  height: '100%',
                  '--controls': 'none',
                  '--media-object-fit': 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}
                primaryColor="transparent"
                secondaryColor="transparent"
                data-testid="hero-video"
              />
            ) : currentFeatured.youtube_id ? (
              <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
                <iframe
                  key={currentFeatured.id}
                  title={`${currentFeatured.title} preview`}
                  src={`https://www.youtube.com/embed/${currentFeatured.youtube_id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${currentFeatured.youtube_id}&playsinline=1&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3&disablekb=1`}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  frameBorder="0"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  style={{
                    width: '177.78vh',
                    height: '56.25vw',
                    minWidth: '100%',
                    minHeight: '100%',
                    pointerEvents: 'none',
                  }}
                  data-testid="hero-video"
                />
              </div>
            ) : (currentFeatured.trailer_url || currentFeatured.video_url) ? (
              <video
                ref={videoRef}
                key={currentFeatured.id}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
                data-testid="hero-video"
                poster={currentFeatured.thumbnail}
              >
                <source 
                  src={currentFeatured.trailer_url || currentFeatured.video_url} 
                  type="application/x-mpegURL" 
                />
              </video>
            ) : (
              <img 
                src={currentFeatured.thumbnail} 
                alt={currentFeatured.title}
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
          </div>

          {/* Content */}
          <div className="relative h-full max-w-7xl mx-auto px-4 md:px-8 flex items-center">
            <div className="max-w-2xl space-y-6">
              {/* Title */}
              <h1 
                className={`text-5xl md:text-7xl font-bold text-foreground drop-shadow-2xl transition-all duration-300 ${
                  isTransitioning ? 'opacity-0 -translate-x-10' : 'opacity-100 translate-x-0'
                }`}
              >
                {currentFeatured.title}
              </h1>

              {/* Metadata */}
              <div 
                className={`flex items-center space-x-4 text-sm text-foreground/90 backdrop-blur-sm bg-black/20 w-fit px-4 py-2 rounded-lg transition-all duration-300 delay-75 ${
                  isTransitioning ? 'opacity-0 -translate-x-10' : 'opacity-100 translate-x-0'
                }`}
              >
                <span className="text-electric font-semibold">Featured</span>
                <span>{currentFeatured.year}</span>
                <span>{currentFeatured.duration}</span>
                <div className="flex items-center space-x-1">
                  <span className="text-electric">★</span>
                  <span>{currentFeatured.rating.toFixed(1)}</span>
                </div>
              </div>

              {/* Description */}
              <p 
                className={`text-lg text-foreground/90 line-clamp-3 drop-shadow-lg transition-all duration-300 delay-100 ${
                  isTransitioning ? 'opacity-0 -translate-x-10' : 'opacity-100 translate-x-0'
                }`}
              >
                {currentFeatured.description}
              </p>

              {/* Genres */}
              <div 
                className={`flex flex-wrap gap-2 transition-all duration-300 delay-150 ${
                  isTransitioning ? 'opacity-0 -translate-x-10' : 'opacity-100 translate-x-0'
                }`}
              >
                {currentFeatured.genre.map((genre, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 text-sm text-foreground shadow-lg"
                    style={{ backdropFilter: 'blur(20px) saturate(180%)' }}
                  >
                    {genre}
                  </span>
                ))}
              </div>

              {/* Buttons */}
              <div 
                className={`flex items-center space-x-4 transition-all duration-300 delay-200 ${
                  isTransitioning ? 'opacity-0 -translate-x-10' : 'opacity-100 translate-x-0'
                }`}
              >
                <button
                  onClick={() => navigate(`/content/${currentFeatured.id}`)}
                  className="flex items-center space-x-2 px-8 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-all hover:scale-105 shadow-xl shadow-electric/30"
                  data-testid="featured-play-button"
                >
                  <Play className="h-5 w-5 fill-background" />
                  <span>Watch Now</span>
                </button>
                <button
                  onClick={() => handleMoreInfo(currentFeatured)}
                  className="flex items-center space-x-2 px-6 py-3 rounded-xl bg-white/10 backdrop-blur-3xl border border-white/20 text-foreground font-semibold hover:bg-white/20 transition-all shadow-xl"
                  style={{ backdropFilter: 'blur(40px) saturate(180%)' }}
                  data-testid="featured-info-button"
                >
                  <Info className="h-5 w-5" />
                  <span>More Info</span>
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Dots */}
          {featuredContent.length > 1 && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center space-x-3">
              {featuredContent.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentFeaturedIndex 
                      ? 'bg-electric w-8' 
                      : 'bg-white/40 hover:bg-white/60'
                  }`}
                  data-testid={`hero-dot-${index}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content Rows */}
      <div className="py-8 space-y-8">
        {isAuthenticated && <ContinueWatchingRow />}
        <ContentRow title="Trending Now" content={trending} />
        {ppvContent.length > 0 && <PPVRow title="Pay-Per-View" content={ppvContent} />}
        {musicVideos.length > 0 && <MusicVideoRow title="Videos" content={musicVideos} />}
        <ContentRow title="New Releases" content={newReleases} />
        <ContentRow title="Pulse Originals" content={pulseOriginals} />
      </div>

      {/* More Info Modal */}
      {modalContent && (
        <ContentInfoModal 
          content={modalContent} 
          onClose={() => setModalContent(null)} 
        />
      )}
    </div>
  );
};

export default Home;
